// ENG-5730: aligned with the DB CHECK constraint on agent_kanban_items.status
// (migration 20260530000003). Previously this union omitted 'cancelled' and
// 'needs_attention' — both are valid persisted states (agents reach 'cancelled'
// via kanban_cancel; the stale-item reaper writes 'needs_attention'), so the
// type silently disagreed with the database. The state machine in
// `kanban/state-machine.ts` keys off this full set.
//
// ENG-7493 (ADR-0044): 'waiting' is work that has started but is parked on a
// human decision or an external dependency (a PR review, an approval). It is a
// non-terminal, non-active state, so it falls out of the 30-min auto-fail, the
// work-loop resume, the stale-lease reaper, and the 24h age-off with no logic
// change (see kanban/state-machine.ts). DB CHECK widened in migration
// 20260709000003.
export type KanbanStatus =
  | 'backlog'
  | 'todo'
  | 'in_progress'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'needs_attention'
  | 'waiting';
export type KanbanSource = 'cron' | 'chat' | 'manual' | 'integration';

export interface KanbanItem {
  id: string;
  agent_id: string;
  team_id: string;
  title: string;
  description?: string;
  priority: number;          // 1=high, 2=medium, 3=low
  status: KanbanStatus;
  estimated_minutes?: number;
  notes?: string;
  source: KanbanSource;
  source_integration?: string;   // e.g., 'linear', 'github'
  source_external_id?: string;   // ID in the external system
  source_url?: string;           // deep link to external source
  last_synced_at?: string;       // ISO timestamp of last upstream refresh (ENG-4604)
  deliverable?: string;
  result?: string;
  notify_channel?: string;
  notify_to?: string;
  /**
   * ADR-0017: optional link to a `projects` container. Nullable; tagging is
   * Phase 2 (an optional arg on kanban_create), so most rows carry null.
   */
  project_id?: string | null;
  started_at?: string;
  completed_at?: string;
  /**
   * ENG-4507: actor that last set this row's status. Subscribers filter on
   * this to suppress agent self-completion round-trips.
   *
   * Format:
   *   - "agent:<agent_id>" — set by MCP write paths (kanban_done, kanban_update)
   *   - "user:<user_id>"   — set by webapp PATCH from the console kanban board
   *   - undefined / null   — legacy rows, treated as "unknown actor" (deliver)
   */
  last_actor_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * ENG-4507: realtime kanban completion event surfaced via Supabase Realtime
 * `postgres_changes` on `agent_kanban_items`. Subscribers (the manager
 * daemon, integration tests) derive this from the row diff — there is no
 * separate emitted schema. Centralised here so every reader uses the same
 * shape and the contract can evolve in one place.
 */
export interface KanbanCompletionEvent {
  agent_id: string;
  item_id: string;
  status: 'done' | 'failed';
  last_actor_id: string | null;
  completed_at: string | null;
  title: string;
}

/**
 * Build the canonical `last_actor_id` value for a status write. Keeping the
 * formatting in one helper means MCP and webapp paths can't drift on the
 * "agent:" / "user:" prefix convention.
 */
export function formatActorId(kind: 'agent' | 'user', id: string): string {
  return `${kind}:${id}`;
}

/**
 * True when an agent should ignore a completion event because it was that
 * same agent that closed the row. Subscribers call this to short-circuit
 * before forwarding the notification into the runtime.
 */
export function isSelfCompletion(event: KanbanCompletionEvent): boolean {
  return event.last_actor_id === formatActorId('agent', event.agent_id);
}

/**
 * ENG-4515: classify the actor on a kanban row from the perspective of a
 * specific agent. Used by `kanban_list` to surface a `closed_by` annotation
 * so the agent can tell user-driven closures apart from its own and stop
 * redoing work the user has already handled.
 *
 * Returns:
 *   - 'self'    — this agent closed the row (suppress redo logic — but the agent
 *                 already knows; the annotation is just informational)
 *   - 'user'    — a human closed the row from the console; the work is done
 *   - 'other'   — a different agent on the team closed it
 *   - 'unknown' — legacy row with no actor recorded; assume external closure
 */
export function classifyActor(
  lastActorId: string | null | undefined,
  selfAgentId: string,
): 'self' | 'user' | 'other' | 'unknown' {
  if (!lastActorId) return 'unknown';
  if (lastActorId === formatActorId('agent', selfAgentId)) return 'self';
  if (lastActorId.startsWith('user:')) return 'user';
  if (lastActorId.startsWith('agent:')) return 'other';
  return 'unknown';
}

/**
 * ENG-10373: the ONE formatter for "which agent did this", as a person or an
 * agent reads it. `@pepper`.
 *
 * It exists so the two recipients of an assignment are told the same identity
 * by construction rather than by two call sites happening to agree on a
 * template literal. A human recipient has been told `@<assigner>` since
 * kanban-assign-human-notify; an agent recipient was told nothing (CS-1711).
 * Both now go through here.
 */
export function formatAgentLabel(codeName: string): string {
  return `@${codeName}`;
}

/**
 * ENG-10373: who ASSIGNED a card, read from the author stamp `kanban_assign`
 * writes (`metadata.assigned_from_code_name` / `assigned_from_agent_id`,
 * packages/api/src/lib/kanban-assign.ts).
 *
 * Deliberately NOT derived from `last_actor_id`. That column is the LAST actor,
 * not the author: every later move overwrites it, the stale-item reaper stamps
 * `system:kanban-stale-reaper` and a console click stamps `user:*`. An author
 * read from it would be right at creation and silently wrong after the first
 * touch, which is worse than no author at all.
 *
 * Degrades rather than throws, because `metadata` is free-form JSON:
 *   - code_name present   -> `@code_name`
 *   - only the id present -> `agent <id>` (a deleted or cross-org assigner still
 *                            names something checkable, never an empty string)
 *   - neither             -> null, meaning "not recorded"
 * No directory lookup: the code_name is stamped alongside the id at write time,
 * so a renderer never has to resolve one.
 */
export function kanbanAssignerLabel(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const m = metadata as Record<string, unknown>;
  const codeName =
    typeof m['assigned_from_code_name'] === 'string' ? m['assigned_from_code_name'].trim() : '';
  if (codeName) return formatAgentLabel(codeName.replace(/^@+/, ''));
  const agentId =
    typeof m['assigned_from_agent_id'] === 'string' ? m['assigned_from_agent_id'].trim() : '';
  if (agentId) return `agent ${agentId}`;
  return null;
}

/**
 * The card came in through direct chat and carries no author stamp. That is the
 * CS-1711 shape: the session id in `source_external_id` names no agent, so the
 * honest answer is "unknown", said out loud. Silence is what let the recipient
 * infer an author and misdeliver.
 */
export const KANBAN_ASSIGNER_UNKNOWN_DIRECT_CHAT =
  'unknown (arrived via direct chat; no assigning agent is recorded)';

/** Single-card read of a card with no author stamp: say so rather than omit the line. */
export const KANBAN_ASSIGNER_NOT_RECORDED =
  'not recorded (no kanban_assign author on this card; it was created on this board or by a path that does not stamp one)';

/**
 * ENG-10373: what to print after "assigned by:" for a card, or null to print
 * nothing.
 *
 * `always` separates the two kinds of surface:
 *   - a single-card read (`kanban_get`) always answers, with an explicit
 *     "not recorded" when there is no stamp;
 *   - a multi-card view (`kanban_list`, `kanban_search`) prints the line only
 *     when it carries information: a recorded author, or a direct-chat card
 *     whose author is genuinely unknown. A card the agent `kanban_add`ed for
 *     itself has no author to be unknown about, and repeating "not recorded"
 *     on every such card every 5 minutes would train the reader to skip the
 *     clause on the cards where it matters.
 */
export function describeKanbanAssigner(
  card: { metadata?: unknown; source_integration?: string | null },
  opts: { always: boolean },
): string | null {
  const label = kanbanAssignerLabel(card.metadata);
  if (label) return label;
  if (card.source_integration === 'direct-chat') return KANBAN_ASSIGNER_UNKNOWN_DIRECT_CHAT;
  return opts.always ? KANBAN_ASSIGNER_NOT_RECORDED : null;
}
